import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { designByIdSelector } from '~/app/store/design'
import { designPersonalizationContextIdSelector, dpcsLoadingSelector } from '~/app/store/designPersonalization/selectors'
import { filterByIdSelector } from '~/app/store/filter'
import { categoryByIdSelector } from '~/app/store/filterCategories'
import { globalQuickViewDataSelector } from '~/app/store/quickView/selectors'
import { selectQuickViewFilterNames, quickViewFilterByIdSelector } from '~/app/store/quickViewFilter'
import { getMpvid } from '~/app/store/config'
import { getSelectedDesignId } from '~/app/store/debug/reducer'
import { useQuantities } from '~/app/features/Gallery/hooks/useQuantities'
import { QUICKVIEW_FILTER_TYPE } from '~/shared/constants'
import { getInitialColorSwatchId } from '../utils'
import { effectiveDesignCreationTypesSelector } from '~/app/store/designCreationTypes/selectors'
import { previouslyOrderedTimestampSelector } from '~/app/store/previouslyOrdered/selectors'
import { isPreviouslyOrderedTemplate } from '~/shared/sets/previouslyOrderedTemplate'

export const useNewQuickViewProps = (tileEntity: State.TileEntity | null) => {
  const designById = useSelector(designByIdSelector)
  const selectedDesignId = useSelector(getSelectedDesignId)
  const quickViewFilterNames = useSelector(selectQuickViewFilterNames)
  const quickViewFilterById = useSelector(quickViewFilterByIdSelector)
  const filterById = useSelector(filterByIdSelector)
  const categoryById = useSelector(categoryByIdSelector)
  const mpvId = useSelector(getMpvid)
  const { locale, quantity } = useSelector(globalQuickViewDataSelector)
  const designPersonalizationContextId = useSelector(designPersonalizationContextIdSelector)
  const dpcsLoading = useSelector(dpcsLoadingSelector)
  const effectiveDesignCreationTypes = useSelector(effectiveDesignCreationTypesSelector)
  const previouslyOrderedTimestamp = useSelector(previouslyOrderedTimestampSelector)

  const { productQuantity } = useQuantities({
    productKey: tileEntity?.productKey,
    productVersion: tileEntity?.productVersion,
    locale,
    quantity,
  })

  const selectedDesignConceptId = useMemo(() => {
    if (!tileEntity) {
      return undefined
    }

    const initialColorSwatchId = getInitialColorSwatchId(tileEntity, selectedDesignId, designById)
    return designById(initialColorSwatchId)?.previewInfo?.templateToken || tileEntity.previewInfo?.templateToken
  }, [tileEntity, selectedDesignId, designById])

  const taxonomyFilters = useMemo(() => {
    if (!tileEntity?.taxonomyVariants) {
      return undefined
    }
    const selectedTaxonomyIds = Object.keys(tileEntity.taxonomyVariants)

    return quickViewFilterNames.flatMap((name) => {
      const qvFilter = quickViewFilterById(name)
      if (qvFilter.quickViewFilterType !== QUICKVIEW_FILTER_TYPE.RADIO) return []

      const filter = filterById(qvFilter.name) as State.CategoryFilter
      if (!filter.taxonomyId || !selectedTaxonomyIds.includes(filter.taxonomyId)) return []

      const rootCategory = categoryById(String(filter.categoryId))
      const categories: Record<string, string> = {}
      rootCategory?.children?.forEach((childId: string) => {
        const child = categoryById(childId)
        if (child) categories[childId] = child.title
      })

      return [{ taxonomyId: filter.taxonomyId, title: qvFilter.title, categories, quickViewSortOrder: qvFilter.quickViewSortOrder }]
    })
  }, [tileEntity, quickViewFilterNames, quickViewFilterById, filterById, categoryById])

  const designPersonalization = useMemo(
    () => ({
      designPersonalizationContextId,
      dpcsLoading,
    }),
    [designPersonalizationContextId, dpcsLoading],
  )

  const selectedOptions = useMemo(() => {
    if (!tileEntity) {
      return undefined
    }

    const options = { ...tileEntity.productOptions }

    if (tileEntity.numberOfPlaceholders) {
      options.numberofphotos = tileEntity.numberOfPlaceholders
    }

    if (tileEntity.taxonomyVariants && taxonomyFilters) {
      const variableTaxonomyIdsSet = new Set(taxonomyFilters.map((tf) => tf.taxonomyId))
      Object.entries(tileEntity.taxonomyVariants).forEach(([taxonomyId, categoryId]) => {
        if (variableTaxonomyIdsSet.has(taxonomyId)) {
          options[taxonomyId] = categoryId
        }
      })
    }

    return options
  }, [tileEntity, taxonomyFilters])

  const previouslyOrderedDate = isPreviouslyOrderedTemplate(tileEntity?.rankingSet) && previouslyOrderedTimestamp ? previouslyOrderedTimestamp : undefined

  return {
    selectedDesignConceptId,
    mpvId,
    productQuantity,
    locale,
    designPersonalization,
    selectedOptions,
    taxonomyFilters,
    designCreationTypes: effectiveDesignCreationTypes ?? undefined,
    previouslyOrderedDate,
  }
}
